<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes } = useMessageContext();
const { t } = useI18n();

const items = computed(() => contentAttributes.value.items || []);

const isOrder = computed(
  () => contentAttributes.value.type === 'whatsapp_order'
);

const currency = computed(
  () => items.value.find(item => item.currency)?.currency
);

// Order webhooks carry per-unit price and quantity; catalog sends carry neither.
const orderTotal = computed(() =>
  items.value.reduce(
    (total, item) => total + (item.price || 0) * (item.quantity || 1),
    0
  )
);

const formatPrice = value => {
  if (!currency.value) return value;
  return `${value} ${currency.value}`;
};

const itemLabel = item =>
  item.name || item.retailer_id || t('CONVERSATION.WHATSAPP_PRODUCTS.ITEM');

const itemSubLabel = item => {
  if (item.quantity) return `${item.quantity} × ${formatPrice(item.price)}`;
  if (item.price) return formatPrice(item.price);
  return item.retailer_id;
};

const totalLabel = computed(() =>
  t('CONVERSATION.WHATSAPP_PRODUCTS.TOTAL', {
    total: formatPrice(orderTotal.value),
  })
);
</script>

<template>
  <BaseBubble class="p-3">
    <div class="flex flex-col gap-2 min-w-56">
      <div class="flex items-center gap-2">
        <Icon
          :icon="isOrder ? 'i-lucide-shopping-cart' : 'i-lucide-shopping-bag'"
          class="size-4"
        />
        <span class="text-sm font-medium">
          {{
            isOrder
              ? t('CONVERSATION.WHATSAPP_PRODUCTS.ORDER_TITLE')
              : t('CONVERSATION.WHATSAPP_PRODUCTS.SENT_TITLE')
          }}
        </span>
      </div>

      <div
        v-for="item in items"
        :key="item.retailer_id"
        class="flex items-center gap-2"
      >
        <img
          v-if="item.image_url"
          :src="item.image_url"
          class="object-cover rounded size-10"
          alt=""
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm truncate">{{ itemLabel(item) }}</span>
          <span class="text-xs opacity-70">{{ itemSubLabel(item) }}</span>
        </div>
      </div>

      <div v-if="isOrder && orderTotal" class="pt-2 border-t border-current/20">
        <span class="text-sm font-medium">{{ totalLabel }}</span>
      </div>

      <p v-if="content" class="text-sm whitespace-pre-line">{{ content }}</p>
    </div>
  </BaseBubble>
</template>
